import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { asset } from "@/lib/assets";

const headerImg = asset("case-studies/liveActivities/Header.png");
const explorationsImg = asset("case-studies/liveActivities/Explorations.png");
const resultsImg = asset("case-studies/liveActivities/Results.png");
const impactImg = asset("case-studies/liveActivities/Impact.png");

// ─── Image helpers ────────────────────────────────────────────────────────────

function ImageSlot({
  src,
  alt,
  className = "",
  caption,
}: {
  src: string;
  alt: string;
  className?: string;
  caption?: string;
}) {
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <div className="w-full">
        <div
          className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
          onClick={() => setLightbox(true)}
          title="Click to expand"
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              el.style.display = "none";
              const parent = el.parentElement;
              if (parent && !parent.querySelector(".placeholder-label")) {
                const label = document.createElement("p");
                label.className =
                  "placeholder-label text-xs text-[#888888] text-center p-4";
                label.textContent = alt;
                parent.appendChild(label);
              }
            }}
          />
        </div>
        {caption && (
          <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
            {caption}
          </p>
        )}
      </div>

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(false)}
          >
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function PlaceholderSlot({
  label,
  className = "",
  caption,
}: {
  label: string;
  className?: string;
  caption?: string;
}) {
  return (
    <div className="w-full">
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl flex items-center justify-center ${className}`}
      >
        <p className="text-xs text-[#888888] text-center p-6">{label}</p>
      </div>
      {caption && (
        <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
          {caption}
        </p>
      )}
    </div>
  );
}

// ─── Side nav ────────────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "explorations", label: "Explorations" },
  { id: "solution", label: "The Solution" },
  { id: "impact", label: "Impact" },
];

function SideNav() {
  const [active, setActive] = useState<string>(NAV_SECTIONS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio);
          } else {
            visibleSections.delete(id);
          }
          if (visibleSections.size > 0) {
            const topmost = [...visibleSections.entries()].reduce((a, b) =>
              a[1] >= b[1] ? a : b,
            );
            setActive(topmost[0]);
          }
        },
        { threshold: [0, 0.25, 0.5], rootMargin: "-10% 0px -60% 0px" },
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav className="hidden xl:flex flex-col gap-1 sticky top-32 self-start w-48 shrink-0">
      {/* Project card */}
      <div className="mb-6 pb-6 border-b border-[#E2E8F0]">
        <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-sm leading-tight mb-0.5">
          HomeLight
        </p>
        <p className="text-xs text-[#888888] leading-snug">
          Agent referral app
          <br />
          iOS Live Activities
        </p>
      </div>

      {NAV_SECTIONS.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById(id)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={`text-xs font-medium py-1.5 pl-3 border-l-2 transition-all duration-200 ${
            active === id
              ? "border-[#1D3FE8] text-[#1A1A1A]"
              : "border-transparent text-[#888888] hover:text-[#1A1A1A] hover:border-[#E2E8F0]"
          }`}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CaseStudyLiveActivities() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
            HomeLight · Agent referral app
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            <span className="text-[#1D3FE8]">Live Activity</span> for iOS
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            Increasing app adoption with timely notifications — surfacing
            time-sensitive referrals on the lock screen and Dynamic Island so
            agents never miss a lead.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-[#E2E8F0]">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Role
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                Lead designer
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Team
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                Product Owner (Gaurav) · Product Designer (me) · Eng team
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Header image */}
      <AnimatedSection>
        <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-20">
          <ImageSlot
            src={headerImg}
            alt="Live Activities for iOS — lock screen and Dynamic Island referral experience"
            className="w-full aspect-[16/7]"
          />
        </div>
      </AnimatedSection>

      {/* Sections */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="flex gap-16 items-start">
          <SideNav />
          <div className="flex-1 min-w-0">

            {/* Overview */}
            <AnimatedSection
              id="overview"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Overview
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                A referral claiming flow is how agents accept new leads from
                HomeLight before someone else does. When a new client is
                available, multiple agents are notified, and the fastest to act
                wins.
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                We brought this experience into iOS Live Activities (inspired by
                apps like Uber), surfacing referrals directly on the lock screen
                and Dynamic Island.
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px]">
                Instead of a notification you miss and gets buried, this one
                sticks around until time expires or somebody else claims it.
              </p>
            </AnimatedSection>

            {/* Explorations */}
            <AnimatedSection
              id="explorations"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Explorations
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Embracing constraints.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                Designing for Live Activities came with constraints. Apple
                provides strict guidelines and limited space. I explored ways to
                make urgency clear within the provided space, and play with
                graphics and content so claiming felt instant, not overwhelming.
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
                I proposed using a "Call now" button to connect immediately —
                but this was technically a problem since the flow needs to
                initiate from the app. That's why we changed to "Claim Now" →
                Open app.
              </p>
              <ImageSlot
                src={explorationsImg}
                alt="Live Activity explorations"
                className="w-full aspect-[2/1]"
              />
            </AnimatedSection>

            {/* The Solution */}
            <AnimatedSection
              id="solution"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                The Solution
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                Live Activities with lead information and a CTA to claim it in
                our app.
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10 space-y-2 pl-1">
                <li>Stays visible on the lock and unlocked screen</li>
                <li>Updates in real time</li>
                <li>Follow-up after call with client with an AI score of the call</li>
                <li>When clicking, connects to our app to see more details — driving more app usage</li>
              </ul>
              <ImageSlot
                src={resultsImg}
                alt="Live Activity solution"
                className="w-full aspect-[3600/2846]"
              />
            </AnimatedSection>

            {/* Impact */}
            <AnimatedSection
              id="impact"
              className="py-20"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Impact
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-10">
                Increasing mobile app adoption.
              </h2>

              {/* Text + image side by side */}
              <div className="flex flex-col md:flex-row gap-10 items-start mb-10">
                <div className="flex-1 min-w-0">
                  <ul className="text-[#444] text-[0.9375rem] leading-[1.85] space-y-3 mb-8">
                    {[
                      "15% of claims now come from Live Activities",
                      "Mobile adoption increased from 20% → ~70%",
                      "Positive feedback from agents",
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[#1D3FE8] shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  {/* Callout */}
                  <div className="bg-[#F5C542] rounded-2xl px-6 py-5 max-w-[480px]">
                    <p className="text-[#1A1A1A] font-semibold text-[0.9375rem] leading-[1.7]">
                      👉 Live Activities perform close to push — but with
                      persistent visibility instead of a single interrupt.
                    </p>
                  </div>
                </div>

                {/* Square image slot */}
                <div className="w-full md:w-[480px] shrink-0">
                  <ImageSlot
                    src={impactImg}
                    alt="Live Activity impact"
                    className="w-full aspect-[1800/1364]"
                  />
                </div>
              </div>
            </AnimatedSection>

            {/* Footer nav */}
            <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
              <Link
                to="/#work"
                className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
              >
                ← All case studies
              </Link>
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  Next case study
                </p>
                <Link
                  to="/case-study/ui-examples"
                  className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
                >
                  UI examples →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
